import { useContext, useEffect, useRef, useState } from 'react'
import { Alert, Button, Divider, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import moment from 'moment-timezone'

import { AppContext } from '/providers/app-context'
import { downloadClientCsr, generateClientCsr, getClientCertificates, uploadOwnAppleCertificate } from '/providers/studio-api'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'

const emptyCertificate = {
  apple: { clientManaged: false, configured: false, csrAvailable: false, passTypeId: '', teamId: '', expiryDate: '', replacementDate: '', replacementOverdue: false }
}

const formatDate = value => {
  if (!value) return 'Not set'
  const date = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true)
  return date.isValid() ? date.format('DD MMM YYYY') : 'Invalid date'
}

const AccountCertificates = () => {
  const { apiSource, apiKey, processing, setProcessing, selectedClientId } = useContext(AppContext)
  const [certificate, setCertificate] = useState(emptyCertificate)
  const [loading, setLoading] = useState(true)
  const [pem, setPem] = useState('')
  const [pemName, setPemName] = useState('')
  const inputRef = useRef(null)
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  const loadCertificate = async () => {
    setLoading(true)
    const result = await getClientCertificates({ apiSource, apiKey, clientId: selectedClientId })
    if (result.ok) setCertificate(result.certificate)
    else {
      setCertificate(emptyCertificate)
      showActionError(result.error, 'Error getting certificate details')
    }
    setLoading(false)
  }

  useEffect(() => { loadCertificate() }, [apiSource, apiKey, selectedClientId])

  const downloadCsr = async () => {
    setProcessing(true)
    const result = await downloadClientCsr({ apiSource, apiKey, clientId: selectedClientId })
    if (result.ok) {
      const url = URL.createObjectURL(new Blob([result.csr_pem], { type: 'application/x-pem-file' }))
      const link = document.createElement('a')
      link.href = url
      link.download = result.filename
      link.click()
      URL.revokeObjectURL(url)
      enqueueSnackbar({ message: 'CSR downloaded', variant: 'success' })
    } else showActionError(result.error, 'Error downloading CSR')
    setProcessing(false)
  }

  const generateAndDownloadCsr = async () => {
    const replacePending = certificate.apple.csrAvailable
      ? window.confirm('A CSR is already pending. Generating a new CSR will invalidate it, so Apple will not be able to issue a certificate from the previous CSR. Continue?')
      : false
    if (certificate.apple.csrAvailable && !replacePending) return

    setProcessing(true)
    const result = await generateClientCsr({ apiSource, apiKey, clientId: selectedClientId, replacePending })
    if (result.ok) {
      const url = URL.createObjectURL(new Blob([result.csr_pem], { type: 'application/x-pem-file' }))
      const link = document.createElement('a')
      link.href = url
      link.download = result.filename
      link.click()
      URL.revokeObjectURL(url)
      enqueueSnackbar({ message: 'CSR generated and downloaded', variant: 'success' })
      await loadCertificate()
    } else showActionError(result.error, 'Error generating CSR')
    setProcessing(false)
  }

  const choosePem = async event => {
    const file = event.target.files?.[0]
    if (!file) return
    setPemName(file.name)
    if (file.name.toLowerCase().endsWith('.cer')) {
      const bytes = new Uint8Array(await file.arrayBuffer())
      let binary = ''
      bytes.forEach(byte => { binary += String.fromCharCode(byte) })
      setPem(btoa(binary))
    } else {
      setPem(await file.text())
    }
  }

  const uploadCertificate = async () => {
    setProcessing(true)
    const result = await uploadOwnAppleCertificate({ apiSource, apiKey, clientId: selectedClientId, pem })
    if (result.ok) {
      enqueueSnackbar({ message: 'Apple certificate uploaded', variant: 'success' })
      setPem('')
      setPemName('')
      if (inputRef.current) inputRef.current.value = ''
      await loadCertificate()
    } else showActionError(result.error, 'Error uploading Apple certificate')
    setProcessing(false)
  }

  if (loading) return <div className='p-5'><Alert severity='info'>Loading certificate details…</Alert></div>
  if (!certificate.apple.clientManaged) return <div className='p-5'><Alert severity='info'>Apple signing certificates for this client are managed by Smartix.</Alert></div>

  return (
    <>
      <div className='flex h-full min-h-0 flex-col gap-5 overflow-auto p-5'>
        <Alert severity='info'>Your Apple developer team manages this signing certificate. Download the CSR to request a certificate from Apple, then upload the resulting PEM certificate here.</Alert>
        <div className='flex flex-col gap-2'>
          <Typography variant='h5'>Apple signing certificate</Typography>
          <Typography variant='body2'>Installed: {certificate.apple.configured ? 'Yes' : 'No'}</Typography>
          <Typography variant='body2'>Expiry: {formatDate(certificate.apple.expiryDate)}</Typography>
          <Typography variant='body2' color={certificate.apple.replacementOverdue ? 'error.main' : 'text.primary'}>Replacement due: {formatDate(certificate.apple.replacementDate)}</Typography>
          <Typography variant='body2'>Pass Type ID: {certificate.apple.passTypeId}</Typography>
          <Typography variant='body2'>Team ID: {certificate.apple.teamId}</Typography>
        </div>
        <Divider />
        <div className='flex max-w-xl flex-col gap-4'>
          <Typography variant='h6'>Renew or install certificate</Typography>
          <Button variant='outlined' onClick={generateAndDownloadCsr} disabled={processing}>Generate and download CSR</Button>
          {certificate.apple.csrAvailable ? <Button variant='text' onClick={downloadCsr} disabled={processing}>Download pending CSR again</Button> : null}
          <Alert severity='info'>Generating a CSR does not replace the installed certificate. Upload Apple’s resulting CER file when it is ready.</Alert>
          <input ref={inputRef} type='file' accept='.pem,.cer' onChange={choosePem} />
          {pemName ? <Typography variant='body2'>Selected file: {pemName}</Typography> : null}
          <div><Button variant='contained' onClick={uploadCertificate} disabled={processing || !pem || !certificate.apple.csrAvailable}>Upload CER or PEM certificate</Button></div>
        </div>
      </div>
      <ActionErrorDialog title='Certificate action failed' open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default AccountCertificates
